// ---------------------------------------------------------------------------
// ProjectCreateError — a create-failure error that has ALREADY been surfaced
// to the user (a toast fired at the producer in project-slice.createServerProject).
//
// Lane 3 (F32): the hero create-failure path toasted a specific message in
// project-slice AND a contradictory generic 'Failed to create project' in the
// hero catch (toast-store has no dedup, so both stacked). The producer now
// throws this marked error; the hero catch reads `alreadyToasted` to skip the
// redundant generic toast, and `authExpired` to redirect to sign-in on a
// mid-session JWT expiry.
//
// The marker survives the plain re-throw through start-app-generation-from-
// prompt.ts (no intermediate catch/rewrap between the producer and the hero),
// so a class instance is the simplest carrier.
// ---------------------------------------------------------------------------

export class ProjectCreateError extends Error {
  /** Always true — the producer already fired a user-visible toast for this
   * failure, so downstream catchers MUST NOT re-toast a generic fallback. */
  readonly alreadyToasted = true
  /** True only when the failure was an auth/session expiry ('Not authenticated'
   * mid-session) — signals the consumer to route the user back to sign-in. */
  readonly authExpired: boolean
  /** The humanized message that was toasted at the producer. */
  readonly userMessage: string

  constructor(opts: { userMessage: string; authExpired?: boolean; cause?: unknown }) {
    super(opts.userMessage)
    this.name = 'ProjectCreateError'
    this.userMessage = opts.userMessage
    this.authExpired = opts.authExpired ?? false
    if (opts.cause !== undefined) {
      ;(this as { cause?: unknown }).cause = opts.cause
    }
  }
}

/** Narrowing helper — a caught error carries the already-toasted marker. */
export function isAlreadyToasted(err: unknown): err is ProjectCreateError {
  return err instanceof ProjectCreateError && err.alreadyToasted === true
}

/**
 * Shared catch handler for EVERY createServerProject / startAppGenerationFromPrompt
 * caller (the producer in project-slice always toasts a specific message and
 * throws ProjectCreateError; the marker survives the plain re-throw through
 * start-app-generation-from-prompt.ts).
 *
 * Before this helper only the hero caller read the marker — the other create
 * callers blind-`toast.error('Failed to create project')` in catch, so on a
 * marked failure they stacked a SECOND contradictory generic toast, and on a
 * session expiry they never signed the user out, leaving a silently-dead
 * session (persona-ux-04). Routing every caller through this one helper makes
 * the behavior uniform:
 *
 *   • already-toasted  → the producer already surfaced a specific, humanized
 *     toast; DON'T stack a generic one.
 *   • authExpired      → route back to sign-in (nulling the session makes
 *     App.tsx render <AuthPage/>).
 *   • un-marked        → a genuinely un-surfaced error; show the caller's
 *     generic fallback (default 'Failed to create project', overridable so a
 *     template caller can say 'Failed to create from template').
 *
 * `deps` is injected (rather than importing the toast-store / studio-store
 * here) so this stays a pure, trivially-testable function with no module-graph
 * coupling to the UI stores.
 */
export interface ProjectCreateErrorDeps {
  /** Surface a generic error toast (the caller's fallback copy). */
  toastError: (message: string) => void
  /** End the session (route the user back to sign-in). */
  signOut: () => void
}

export function handleProjectCreateError(
  err: unknown,
  deps: ProjectCreateErrorDeps,
  fallbackMessage = 'Failed to create project',
): void {
  if (isAlreadyToasted(err)) {
    // The producer already fired a specific, humanized toast for this failure.
    if (err.authExpired) {
      deps.signOut()
    }
    return
  }
  // Genuinely un-surfaced (unexpected) error — keep the generic fallback.
  deps.toastError(fallbackMessage)
}
